document.addEventListener('DOMContentLoaded', () => {
  const raw = sessionStorage.getItem('octaneQuizData');

  if (!raw && !window.Shopify?.designMode) {
    window.location.href = '/pages/routine-quiz';
    return;
  }

  const octaneData = raw ? JSON.parse(raw) : null;
  const routineData = normalizeOctaneData(octaneData);
  const productSizeCache = new Map();

  const activeTabs = {
    short: 'am',
    long: 'am'
  };

  /**
   * Toggle between Products and Facials routine views.
   */
  function setRoutineView(view) {
    const isProducts = view === 'products';

    document.getElementById('routine-products-view').hidden = !isProducts;
    document.getElementById('routine-facials-view').hidden = isProducts;

    document.querySelectorAll(`
      .editorial-section.isolate,
      .routine-results__banner,
      .text-content-section
    `).forEach(section => {
      section.hidden = !isProducts;
    });

    document.querySelectorAll('.routine-view-tab')
      .forEach(btn => {
        btn.classList.toggle('is-active', btn.dataset.view === view);
      });

    const url = new URL(window.location);
    url.searchParams.set('tab', view);
    window.history.replaceState({}, '', url);
  }

  /**
   * Handle AM/PM tab switching for each routine column.
   */
  document.querySelectorAll('.routine-tab').forEach(button => {
    button.addEventListener('click', () => {
      const routineKey = button.dataset.routine;
      const tabKey = button.dataset.tab;

      activeTabs[routineKey] = tabKey;

      const column = button.closest('.routine-column');

      column
        .querySelectorAll('.routine-tab')
        .forEach(btn => btn.classList.remove('is-active'));

      button.classList.add('is-active');

      updateBreakdownCopy(column, routineKey, tabKey);
      renderRoutineColumn(routineKey);
    });
  });

  /**
   * Update AM/PM breakdown copy within a routine column.
   */
  function updateBreakdownCopy(column, routineKey, tabKey) {
    const copy = column.querySelector('.routine-results__breakdown-copy');
    if (!copy) return;

    copy.textContent =
      window.quizStrings?.[
        `${routineKey}Breakdown${tabKey.toUpperCase()}`
      ] || '';
  }

  /**
   * Returns a safe empty data structure if quiz data is missing.
   */
  function emptyRoutineData() {
    return {
      answers: {},
      routines: {
        short: { am: [], pm: [] },
        long: { am: [], pm: [] }
      }
    };
  }

  /**
   * Keep routine accordions open on desktop and collapsed on mobile.
   */
  function syncRoutineAccordions() {
    const isMobile = window.innerWidth < 990;

    document.querySelectorAll('.routine-results__accordion')
      .forEach(accordion => accordion.open = !isMobile);
  }

  /**
   * Helper to extract metafield value from product object.
   */
  function getMetafield(product, key, namespace = null) {
    const field = product.metafields?.find(m =>
      namespace ? m.key === key && m.namespace === namespace : m.key === key
    );

    return field?.json_value || field?.value || '';
  }

  /**
   * Extract Shopify product handle from product URL.
   */
  function getHandleFromLink(link) {
    if (!link) return '';

    try {
      const url = new URL(link);
      return url.pathname.split('/').filter(Boolean).pop() || '';
    } catch {
      return '';
    }
  }

  /**
   * Extract "badge" label from product tags.
   */
  function getBadgeFromTags(product) {
    const badgeTag = (product.tags || []).find(
      tag => typeof tag === 'string' && tag.toLowerCase().startsWith('badge:')
    );

    return badgeTag ? badgeTag.split(':')[1].trim() : '';
  }

  /**
   * Match Octane products against configured AM/PM section slots.
   * Short routines combine treatment steps 4/5 into one recommendation.
   */
  function getRoutineProducts(products, routineKey, routineLength) {
    const slots = window.routineSlots?.[routineKey] || [];
    const used = new Set();
    const usedGroups = new Set();

    const matchedProducts = slots
      .filter(slot => !(routineLength === 'short' && slot.long_only))
      .flatMap(slot => {
        const isTreatment = routineLength === 'short' && [4, 5].includes(slot.slot);
        const group = isTreatment ? 'treatment' : `slot-${slot.slot}`;

        if (usedGroups.has(group)) return [];

        const handles = isTreatment && routineKey === 'pm'
          ? slots.filter(s => [4, 5].includes(s.slot)).flatMap(s => s.products)
          : slot.products;

        const matchedProduct =
          products.find(product =>
            product.handle.includes('hyperpigmentation') &&
            handles.includes(product.handle) &&
            !used.has(product.handle)
          ) ||
          products.find(product =>
            handles.includes(product.handle) &&
            !used.has(product.handle)
          );

        if (!matchedProduct) return [];

        used.add(matchedProduct.handle);
        usedGroups.add(group);

        return [{
          ...matchedProduct,
          slot: slot.slot
        }];
      });

    return sortRoutineProducts(matchedProducts, routineKey);
  }

  /**
   * Fetch and cache the selected variant size from Shopify product data.
   */
  async function getVariantSize(product) {
    if (!product.handle || !product.variant_id) return '';

    const cacheKey = `${product.handle}:${product.variant_id}`;

    if (productSizeCache.has(cacheKey)) {
      return productSizeCache.get(cacheKey);
    }

    try {
      const response = await fetch(`/products/${product.handle}.js`);
      if (!response.ok) return '';

      const productData = await response.json();
      const variant = productData.variants.find(
        v => Number(v.id) === Number(product.variant_id)
      );

      if (!variant) return '';

      const sizeIndex = productData.options.findIndex(
        option => option.name.toLowerCase() === 'size'
      );

      if (sizeIndex === -1) return '';

      const size = variant[`option${sizeIndex + 1}`] || '';
      productSizeCache.set(cacheKey, size);

      return size;
    } catch (error) {
      console.error('Failed to get variant size:', error);
      return '';
    }
  }

  /**
   * Populate rendered product cards with their matching variant sizes.
   */
  async function hydrateProductSizes(products, container) {
    for (const product of products) {
      const el = container.querySelector(
        `.routine-card__size[data-variant-id="${product.variant_id}"]`
      );

      if (!el) continue;

      const size = await getVariantSize(product);

      if (size) {
        el.textContent = size;
      }
    }
  }

  /**
   * Convert raw Octane quiz data into long/short AM/PM routine data.
   */
  function normalizeOctaneData(octaneData) {
    if (!octaneData?.product_blocks) return emptyRoutineData();

    const products = octaneData.product_blocks.flat().filter(Boolean);

    const normalizedProducts = products.map(product => ({
      badge: getBadgeFromTags(product),
      handle: getHandleFromLink(product.link),
      id: product.id,
      image: product.image,
      link: product.link,
      price: product.price_formatted || '',
      reviews_average: Number(getMetafield(product, 'reviews_average', 'yotpo')) || null,
      reviews_count: Number(getMetafield(product, 'reviews_count', 'yotpo')) || null,
      title: product.title,
      subtitle: getMetafield(product, 'subtitle'),
      quiz_results_subtitle: getMetafield(product, 'quiz_results_subtitle'),
      variant_id: product.variants?.[0]?.id || null
    }));

    return {
      answers: octaneData.answers || {},
      routines: {
        short: {
          title: window.quizStrings?.shortRoutineTitle,
          subtitle: window.quizStrings?.shortRoutineSubtitle,
          am: getRoutineProducts(normalizedProducts, 'am', 'short'),
          pm: getRoutineProducts(normalizedProducts, 'pm', 'short')
        },
        long: {
          title: window.quizStrings?.fullRoutineTitle,
          subtitle: window.quizStrings?.fullRoutineSubtitle,
          am: getRoutineProducts(normalizedProducts, 'am', 'long'),
          pm: getRoutineProducts(normalizedProducts, 'pm', 'long')
        }
      }
    };
  }

  /**
   * Normalize comma-separated concern list into an array.
   */
  function normalizeList(value) {
    return String(value || '')
      .split(',')
      .map(v => v.toLowerCase().trim().replace(/\s+/g, ' '))
      .filter(Boolean);
  }

  /**
   * Helper to get an answer value by matching part of the question text.
   */
  function getAnswerValue(matchText) {
    const entry = Object.entries(routineData.answers || {}).find(([key]) =>
      key.toLowerCase().includes(matchText.toLowerCase())
    );

    return entry ? entry[1] : null;
  }

  /**
   * Return facial blocks that match selected skin concerns.
   */
  function getSelectedFacials() {
    if (!window.facialContent) return [];

    const selected = new Set(normalizeList(getAnswerValue('skin concerns')));
    if (!selected.size) return [];

    return window.facialContent
      .filter(f => normalizeList(f.skin_concerns).some(c => selected.has(c)))
      .slice(0, 2);
  }

  /**
   * Format the skin concerns for correct display.
   */
  function formatSkinConcerns(concerns = '') {
    return String(concerns)
      .replace('Breakouts, Congestion or Blackheads', 'BREAKOUTS')
      .split(',')
      .map(c => c.trim().replace('BREAKOUTS', 'Breakouts, Congestion or Blackheads'))
      .join(' and ');
  }

  /**
   * Replace # placeholders with selected skin concerns.
   */
  function formatRoutineSubtitle(subtitle = '') {
    const concerns = getAnswerValue('skin concerns');
    if (!concerns) return subtitle;

    const formattedConcerns = formatSkinConcerns(concerns);
    return subtitle.replace('#', formattedConcerns);
  }

  /**
   * Render accordion block inside facial cards.
   */
  function renderAccordion(title, copy, id) {
    if (!title) return '';

    return `
      <div class="accordion no-js">
        <details id="Details-${id}" open>
          <summary id="Summary-${id}" role="button" aria-controls="CollapsibleAccordion-${id}" aria-expanded="false">
            <h3 class="accordion__title inline-richtext h6">${title}</h3>

            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-plus icon-caret" fill="none" viewBox="0 0 10 10">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path>
            </svg>

            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path>
            </svg>
          </summary>

          <div class="accordion__content rte" id="CollapsibleAccordion-${id}" role="region" aria-labelledby="Summary-${id}">
            <p>${copy || ''}</p>
          </div>
        </details>
      </div>
    `;
  }

  /**
   * Render star rating visual.
   */
  function renderStars(rating) {
    const percentage = ((Number(rating) || 0) / 5) * 100;
    return `<span class="routine-stars" style="--rating: ${percentage}%"></span>`;
  }

  /**
   * Check whether a product is eligible for the quiz discount.
   */
  function isProductDiscountable(product) {
    return Boolean(
      window.quizStrings?.discountCode &&
      window.quizDiscountProducts?.includes(product.handle)
    );
  }

  /**
   * Format the money.
   */
  function formatMoney(cents) {
    return Shopify.formatMoney(cents, window.moneyFormat);
  }

  /**
   * Get price in cents.
   */
  function getPriceInCents(price) {
    const numericPrice = Number(
      String(price || '').replace(/[^\d.]/g, '')
    );

    return Math.round(numericPrice * 100);
  }

  /**
   * Render the product price.
   */
  function renderProductPrice(product) {
    if (!isProductDiscountable(product)) {
      return `
        <div class="price__regular">
          <span class="price-item price-item--regular">
            ${product.price}
          </span>
        </div>
      `;
    }

    const price = getPriceInCents(product.price);
    const discountedPrice = Math.round(price * 0.85);

    return `
      <div class="price__regular price--on-sale">
        <s class="price-item price-item--regular routine-price--compare">
          ${product.price}
        </s>

        <span class="price-item price-item--sale">
          ${formatMoney(discountedPrice)}
        </span>
      </div>
    `;
  }

  /**
   * Render individual product card.
   */
  function renderProductCard(product, index) {
    return `
      <div class="card-wrapper product-card mobile-styling">
        <div
          class="card card--standard card--media card--horizontal card--quick-view"
          style="--ratio-percent: 133.33333333333334%;"
        >
          <div class="card__inner bg-white ratio" style="--ratio-percent: 133.33333333333334%;">
            <a href="${product.link}" target="_blank" rel="noopener noreferrer" class="card__media">
              <div class="media media--transparent media--hover-effect">
                <img
                  src="${product.image}"
                  alt="${product.title || ''}"
                  class="motion-reduce"
                  loading="lazy"
                  width="300"
                >
              </div>
            </a>

            <div class="card__badge top left">
              ${product.badge ? `
                <span class="badge badge--bottom-left">
                  ${product.badge}
                </span>
              ` : ''}
            </div>
          </div>

          <div class="card__content">
            <div class="card__information">
            <div class="routine-card__step caption">
              Step ${index + 1}
            </div>

              ${product.reviews_average && product.reviews_count ? `
                <div class="routine-card__reviews">
                  ${renderStars(product.reviews_average)}
                  <span class="routine-card__reviews-count">
                    (${Number(product.reviews_count).toLocaleString('en-GB')})
                  </span>
                </div>
              ` : ''}

              <h3 class="card__heading h6">
                <a
                  href="${product.link}"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="full-unstyled-link card-url"
                >
                  ${product.title}
                </a>
              </h3>

              <div class="card-information">
                <div class="card-information__container">
                  <p class="size routine-card__size" data-variant-id="${product.variant_id}"></p>

                  <div class="price">
                    <div class="price__container">
                      ${renderProductPrice(product)}
                    </div>
                  </div>
                </div>

                ${product.quiz_results_subtitle || product.subtitle ? `
                  <p class="caption">
                    ${product.quiz_results_subtitle || product.subtitle}
                  </p>
                ` : ''}
              </div>
            </div>

            <div class="card__quick-view-buttons">
              <button
                type="button"
                class="card__quick-view button button--tertiary"
                aria-label="Quick view"
                data-product-drawer-trigger
                data-product-handle="${product.handle}"
                data-enable-atb="${window.quizStrings.enableAtb}"
                data-enable-long-version="${window.quizStrings.enableLongVersion}"
              >
                <svg class="icon icon-eye" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.03613 12.322C1.96712 12.1146 1.96712 11.8904 2.03613 11.683C3.42313 7.51 7.36013 4.5 12.0001 4.5C16.6381 4.5 20.5731 7.507 21.9631 11.678C22.0331 11.885 22.0331 12.109 21.9631 12.317C20.5771 16.49 16.6401 19.5 12.0001 19.5C7.36213 19.5 3.42613 16.493 2.03613 12.322Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M15 12C15 12.7956 14.6839 13.5587 14.1213 14.1213C13.5587 14.6839 12.7956 15 12 15C11.2044 15 10.4413 14.6839 9.87868 14.1213C9.31607 13.5587 9 12.7956 9 12C9 11.2044 9.31607 10.4413 9.87868 9.87868C10.4413 9.31607 11.2044 9 12 9C12.7956 9 13.5587 9.31607 14.1213 9.87868C14.6839 10.4413 15 11.2044 15 12Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
              </button>

              <button
                class="button button--primary card__quick-view routine-add-button"
                data-variant-id="${product.variant_id}"
              >
                <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3762 9V4.5C13.3762 3.50544 12.9811 2.55161 12.2779 1.84835C11.5746 1.14509 10.6208 0.75 9.62621 0.75C8.63164 0.75 7.67782 1.14509 6.97456 1.84835C6.27129 2.55161 5.87621 3.50544 5.87621 4.5V9M17.2322 7.007L18.4952 19.007C18.5652 19.672 18.0452 20.25 17.3762 20.25H1.87621C1.71841 20.2502 1.56235 20.2171 1.41815 20.1531C1.27396 20.089 1.14485 19.9953 1.03923 19.8781C0.933604 19.7608 0.85382 19.6227 0.80506 19.4726C0.7563 19.3226 0.739655 19.1639 0.756206 19.007L2.02021 7.007C2.04936 6.73056 2.17983 6.4747 2.38646 6.28876C2.59308 6.10281 2.86123 5.99995 3.13921 6H16.1132C16.6892 6 17.1722 6.435 17.2322 7.007ZM6.25121 9C6.25121 9.09946 6.2117 9.19484 6.14137 9.26517C6.07104 9.33549 5.97566 9.375 5.87621 9.375C5.77675 9.375 5.68137 9.33549 5.61104 9.26517C5.54071 9.19484 5.50121 9.09946 5.50121 9C5.50121 8.90054 5.54071 8.80516 5.61104 8.73483C5.68137 8.66451 5.77675 8.625 5.87621 8.625C5.97566 8.625 6.07104 8.66451 6.14137 8.73483C6.2117 8.80516 6.25121 8.90054 6.25121 9ZM13.7512 9C13.7512 9.09946 13.7117 9.19484 13.6414 9.26517C13.571 9.33549 13.4757 9.375 13.3762 9.375C13.2767 9.375 13.1814 9.33549 13.111 9.26517C13.0407 9.19484 13.0012 9.09946 13.0012 9C13.0012 8.90054 13.0407 8.80516 13.111 8.73483C13.1814 8.66451 13.2767 8.625 13.3762 8.625C13.4757 8.625 13.571 8.66451 13.6414 8.73483C13.7117 8.80516 13.7512 8.90054 13.7512 9Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render facial/editorial card.
   */
  function renderFacialCard(facial, index) {
    return `
      <div class="editorial-section">
        <div class="editorial-section__grid grid grid--1-col grid--2-col-tablet">
          <div class="editorial-section__media-item grid__item">
            <div class="editorial-section__media global-media-settings media height-desktop--large height-mobile--medium">
              ${facial.image ? `
                <img src="${facial.image}" alt="${facial.title || ''}" loading="lazy" width="600">
              ` : ''}
            </div>
          </div>

          <div class="editorial-section__text-item grid__item">
            <div class="editorial-section__content content-container">
              <div class="editorial-section__container" style="max-width: 38rem">
                <div class="editorial-section__text--caption caption small">
                  <p><strong>RECOMMENDED FOR YOU</strong></p>
                </div>

                ${facial.title ? `<h2 class="editorial-section__heading">${facial.title}</h2>` : ''}

                ${facial.button_title ? `
                  <a
                    ${facial.button_link ? `href="${facial.button_link}"` : 'role="link" aria-disabled="true"'}
                    class="button button--primary button--full-width"
                    style="min-height: 4.5rem;"
                  >
                    ${facial.button_title}
                  </a>
                ` : ''}

                ${facial.subtitle ? `
                  <div class="editorial-section__text rte light">
                    <p>${facial.subtitle}</p>
                  </div>
                ` : ''}

                ${renderAccordion(facial.accordion_1_title, facial.accordion_1_copy, `facial-card-${index}-1`)}
                ${renderAccordion(facial.accordion_2_title, facial.accordion_2_copy, `facial-card-${index}-2`)}
                ${renderAccordion(facial.accordion_3_title, facial.accordion_3_copy, `facial-card-${index}-3`)}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render a long or short routine column.
   */
  function renderRoutineColumn(routineKey) {
    const tabKey = activeTabs[routineKey];
    const routine = routineData.routines?.[routineKey];

    const column = document.querySelector(
      `.routine-column[data-routine="${routineKey}"]`
    );

    if (!column || !routine) return;

    const productsContainer = column.querySelector('.routine-products');
    const summaryContainer = column.querySelector('.routine-summary');

    if (!productsContainer || !summaryContainer) return;

    const products = routine[tabKey] || [];

    if (!products.length) {
      const comparison = document.querySelector('.routine-results__content');
      if (comparison) comparison.innerHTML = '<p class="routine-empty">No products available.</p>';
      return;
    }

    summaryContainer.innerHTML = `
      <h2 class="h3 title">${routine.title}</h2>

      <p class="routine-subtitle">
        ${formatRoutineSubtitle(routine.subtitle)}
      </p>

      <button
        class="button button--primary add-routine-to-cart"
        data-routine="${routineKey}"
      >
        ${routineKey === 'short'
          ? window.quizStrings?.essentialsAdd
          : window.quizStrings?.fullRoutineAdd}
      </button>

      ${routineKey === 'short' ? `
        <button
          type="button"
          class="button--plain routine-results__upgrade-link"
          data-scroll-to-routine="long"
        >
          Ready to upgrade?
          <span>Check the Full Routine.</span>
        </button>
      ` : ''}
    `;

   productsContainer.innerHTML = `
    ${products.map(renderProductCard).join('')}

    <div class="routine-products__footer">
      <button
        class="button button--primary add-routine-to-cart"
        data-routine="${routineKey}"
      >
        ${routineKey === 'short'
          ? window.quizStrings?.essentialsAdd
          : window.quizStrings?.fullRoutineAdd}
      </button>
    </div>
  `;
    
    hydrateProductSizes(products, productsContainer);
  }

  /**
   * Render selected facials section.
   */
  function renderFacials() {
    const facialsContainer = document.getElementById('routine-facials');
    if (!facialsContainer) return;

    const facials = getSelectedFacials();

    facialsContainer.innerHTML = facials.length
      ? facials.map(renderFacialCard).join('')
      : '';
  }

  /**
   * Swap PM hyperpigmentation before retinoid when both are recommended.
   */
  function sortRoutineProducts(products, routineKey) {
    if (routineKey !== 'pm') return products;

    const sorted = [...products];
    const step4Index = sorted.findIndex(product => product.slot === 4);

    const hyperpigIndex = sorted.findIndex(
      product =>
        product.slot === 5 &&
        product.handle === 'the-hyperpigmentation-serum'
    );

    if (step4Index > -1 && hyperpigIndex > -1) {
      [sorted[step4Index], sorted[hyperpigIndex]] = [
        sorted[hyperpigIndex],
        sorted[step4Index]
      ];
    }
    return sorted;
  }

  /**
   * Add multiple items to cart via Shopify AJAX API.
   */
  async function addItemsToCart(items, button) {
    if (!items?.length) return;

    const originalText = button.innerHTML;

    try {
      button.disabled = true;
      button.innerHTML = `
        <svg
          aria-hidden="true"
          focusable="false"
          class="spinner"
          viewBox="0 0 66 66"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      `;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

      await response.json();
      await applyDiscountCode(window.quizStrings?.discountCode);
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        button.disabled = false;
        button.innerHTML = originalText;
      }, 1500);
    }
  }

  /**
   * Add full active routine to cart.
   */
  function addRoutineToCart(products, button) {
    const seen = new Set();

    const items = products
      .filter(product => {
        const id = Number(product.variant_id);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map(product => ({
        id: Number(product.variant_id),
        quantity: 1
      }));

    addItemsToCart(items, button);
  }

  /**
   * Apply a discount code to the current cart.
   */
  async function applyDiscountCode(code) {
    if (!code) return;

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        discount: code
      })
    });
  }

  /**
   * Sends quiz results to Make so Klaviyo / Shopify can be updated server-side.
   */
  function sendQuizDataToKlaviyo() {
    const email = octaneData?.email;
    if (!email) return;

    const quizAnswers = {
      ...Object.fromEntries(
        Object.entries(routineData.answers || {})
          .filter(([key]) => !key.toLowerCase().includes('quiz results url'))
      ),
      'quiz: skin concerns': formatSkinConcerns(routineData.answers?.['quiz: skin concerns']),
      'quiz: routine short am': routineData.routines.short.am.map(p => p.title).join(', '),
      'quiz: routine short pm': routineData.routines.short.pm.map(p => p.title).join(', '),
      'quiz: routine long am': routineData.routines.long.am.map(p => p.title).join(', '),
      'quiz: routine long pm': routineData.routines.long.pm.map(p => p.title).join(', ')
    };
    
    const quizData = {
      email,
      quiz_answers: quizAnswers,
      email_consent: Boolean(octaneData?.email_consent),
      quiz_facials_json: JSON.stringify(getSelectedFacials() || []),
      quiz_routine_json: JSON.stringify(routineData.routines || {})
    };

    const payloadKey = 'octaneQuizPayload';
    const payloadString = JSON.stringify(quizData);

    if (sessionStorage.getItem(payloadKey) === payloadString) return;

    fetch('https://hook.eu2.make.com/g5lxi61o0ph4msj1myh2e3kqacr0shya', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payloadString
    }).catch(error => {
      console.error('Failed to send quiz data to Klaviyo webhook', error);
    });

    sessionStorage.setItem(payloadKey, payloadString);
  }

  /**
   * Handle add-to-cart interactions.
   */
  document.addEventListener('click', event => {
    const productBtn = event.target.closest('.routine-add-button');

    if (productBtn) {
      addItemsToCart(
        [{ id: Number(productBtn.dataset.variantId), quantity: 1 }],
        productBtn
      );
      return;
    }

    const routineBtn = event.target.closest('.add-routine-to-cart');

    if (routineBtn) {
      const routineKey = routineBtn.dataset.routine;
      const routine = routineData.routines?.[routineKey] || {};

      const products = [
        ...(routine.am || []),
        ...(routine.pm || [])
      ];

      addRoutineToCart(products, routineBtn);
    }
  });

  /**
   * Scroll to the full routine column from the essentials upgrade link.
   */
  document.addEventListener('click', event => {
    const upgradeLink = event.target.closest('[data-scroll-to-routine]');

    if (!upgradeLink) return;

    const target = document.querySelector(
      '.routine-column[data-routine="long"]'
    );

    if (!target) return;

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  });

  /**
   * Initial render.
   */
  syncRoutineAccordions();
  renderRoutineColumn('short');
  renderRoutineColumn('long');
  renderFacials();

  document.querySelectorAll('.routine-column').forEach(column => {
    const routineKey = column.dataset.routine;
    updateBreakdownCopy(column, routineKey, activeTabs[routineKey]);
  });

  sendQuizDataToKlaviyo();

  /**
   * Initialize routine view from URL params and tab interactions.
   */
  setRoutineView(
    new URLSearchParams(window.location.search).get('tab') === 'facials'
      ? 'facials'
      : 'products'
  );

  document.querySelectorAll('.routine-view-tab').forEach(button => {
    button.addEventListener('click', () => {
      setRoutineView(button.dataset.view);
    });
  });
});